import React, { useState, useEffect, useRef } from 'react';
import { cn } from '@rocco/utils/cn';
import DetailBannerSection from '~/modules/page-builder/component/detail/detail-banner-section/DetailBannerSection';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import {
    BANNER_WIDTH_FULL_MOBILE,
    FULL_COLUMN_WIDTH_FULL_SCREEN,
} from '~/modules/page-builder/config/column-constants';
import DownloadAction from '~/modules/page-builder/component/detail/detail-banner-section/actions/DownloadAction';
import { getBannerDimensions } from '~/utils/banner-utils';
import type { ProjectPageLoader } from '@modules/project/type/loader';
import { useUser } from '~/modules/user';
import { useAIUpscaleApi } from '../../hook/use-ai-upscale-api';
import { useAIRevertApi } from '../../hook/use-ai-revert-api';
import { Button } from '@rocco/components/button';
import { Check, LoaderCircle, Sparkles, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { useLogger } from '@archipro-website/logger/client';

interface ProjectBannerProps {
    canDownloadBannerImage: boolean;
    project: ProjectPageLoader['project'];
}

/**
 * Banner component that displays the project banner image
 */
const ProjectBanner = ({
    canDownloadBannerImage,
    project,
}: ProjectBannerProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const bannerRef = useRef<HTMLDivElement>(null);
    const [isImageUpscaled, setIsImageUpscaled] = useState(false);

    const user = useUser();
    const isAPStaff = user.__typename === 'Me' && user.IsArchiproStaff;
    const logger = useLogger();

    const originalImageUrl = project.Banner?.imageUrl ?? '';
    const videoUrl = project.Banner?.videoBannerUrl;

    const bannerSize = getImagesSizes({
        desktop: FULL_COLUMN_WIDTH_FULL_SCREEN,
        mobile: BANNER_WIDTH_FULL_MOBILE,
    });
    const bannerDimensions = getBannerDimensions('project');

    // Check hover state to display the download and upscale buttons
    useEffect(() => {
        const handleMouseEnter = () => {
            setIsHovered(true);
        };
        const handleMouseLeave = () => {
            setIsHovered(false);
        };

        const bannerElement = bannerRef.current;
        if (bannerElement) {
            bannerElement.addEventListener('mouseenter', handleMouseEnter);
            bannerElement.addEventListener('mouseleave', handleMouseLeave);
        }

        return () => {
            if (bannerElement) {
                bannerElement.removeEventListener(
                    'mouseenter',
                    handleMouseEnter
                );
                bannerElement.removeEventListener(
                    'mouseleave',
                    handleMouseLeave
                );
            }
        };
    }, []);

    const bannerLibraryImageID = project.Banner.libraryImage?.ID ?? 0;
    const [triggerUpscale, { loading: upscaleLoading }] =
        useAIUpscaleApi(bannerLibraryImageID);
    const [triggerRevert, { loading: revertLoading }] = useAIRevertApi(
        bannerLibraryImageID,
        originalImageUrl
    );

    // If no banner image, don't render anything
    if (!project.Banner?.imageUrl && !videoUrl) {
        return null;
    }

    return (
        <div className="w-full">
            <div ref={bannerRef} className="relative select-none">
                {
                    <div
                        className={cn(
                            'group relative aspect-[16/9] w-full overflow-hidden bg-black md:max-h-[120vh]'
                        )}
                    >
                        <DetailBannerSection
                            {...project.Banner}
                            imageUrl={project.Banner?.imageUrl}
                            title={project.Title}
                            dimensions={bannerDimensions}
                            size={bannerSize}
                            isVideoBanner={
                                project.Banner?.isVideoBanner || !!videoUrl
                            }
                            videoBannerUrl={videoUrl ?? ''}
                            playsInlineVideoBanner={true}
                            autoPlayVideoBanner={true}
                            loopVideoBanner={true}
                            hideVideoControls={true}
                            showBackButton={false}
                            awards={project.Awards}
                        />

                        {/* Overlay div */}
                        {project.Banner?.overlay && (
                            <div className="z-45 absolute inset-0 bg-black/20" />
                        )}

                        <div
                            className={cn(
                                'absolute bottom-8 right-12 z-50 flex min-h-8 min-w-8'
                            )}
                            style={{
                                opacity: isHovered ? 1 : 0,
                                transition: 'opacity 0.1s ease-in-out',
                            }}
                        >
                            {isAPStaff && canDownloadBannerImage && (
                                <div className="flex h-full items-end">
                                    <div className="mr-8 flex w-48 flex-col items-center">
                                        {isImageUpscaled && (
                                            <Button
                                                className="mb-2 h-12 border-none bg-gray-100/50 backdrop-blur-xl hover:bg-gray-100"
                                                size="narrow"
                                                onClick={() =>
                                                    triggerRevert({
                                                        onCompleted: () => {
                                                            setIsImageUpscaled(
                                                                false
                                                            );
                                                            toast.success(
                                                                'Image successfully reverted!'
                                                            );
                                                        },
                                                        onError: (
                                                            error: string
                                                        ) => {
                                                            logger.error(
                                                                'AI Revert Error',
                                                                {
                                                                    error,
                                                                    projectId:
                                                                        project.ID,
                                                                    libraryImageId:
                                                                        bannerLibraryImageID,
                                                                }
                                                            );
                                                            toast.error(
                                                                `Failed to revert image: ${error}`
                                                            );
                                                        },
                                                    })
                                                }
                                                disabled={revertLoading}
                                            >
                                                Click to revert
                                                <Undo2 className="ml-2 h-4 w-4" />
                                            </Button>
                                        )}
                                        <Button
                                            className="h-12 border-none bg-gray-100/50 backdrop-blur-xl hover:bg-gray-100"
                                            style={{
                                                opacity: 1,
                                            }}
                                            onClick={() =>
                                                triggerUpscale({
                                                    onCompleted: (data) => {
                                                        if (
                                                            !data.imageUpscale
                                                                .url ||
                                                            !data.imageUpscale
                                                                .fileKey
                                                        ) {
                                                            const fullErrorMessage =
                                                                data
                                                                    .imageUpscale
                                                                    .Message ||
                                                                'Unknown error occurred';

                                                            // Log the full error for debugging
                                                            logger.warning(
                                                                'AI Upscale Error',
                                                                {
                                                                    error: fullErrorMessage,
                                                                    projectId:
                                                                        project.ID,
                                                                    libraryImageId:
                                                                        bannerLibraryImageID,
                                                                }
                                                            );

                                                            toast.error(
                                                                'Failed to upscale image. Please try again later or post in #support.'
                                                            );
                                                            return;
                                                        }

                                                        toast.success(
                                                            'Image successfully upscaled!'
                                                        );
                                                        setIsImageUpscaled(
                                                            true
                                                        );
                                                    },
                                                    onError: (
                                                        error: string
                                                    ) => {
                                                        // Log the full error for debugging
                                                        logger.warning(
                                                            'AI Upscale Error',
                                                            {
                                                                error,
                                                                projectId:
                                                                    project.ID,
                                                                libraryImageId:
                                                                    bannerLibraryImageID,
                                                            }
                                                        );
                                                        toast.error(
                                                            'Failed to upscale image. Please try again later or post in #support.'
                                                        );
                                                    },
                                                })
                                            }
                                            disabled={
                                                upscaleLoading ||
                                                isImageUpscaled
                                            }
                                            color="secondary"
                                            size="default"
                                        >
                                            {isImageUpscaled
                                                ? 'Resolution Boosted'
                                                : upscaleLoading
                                                  ? 'Enhancing Quality'
                                                  : 'Boost Resolution'}
                                            {isImageUpscaled ? (
                                                <Check className="ml-2 h-4 w-4" />
                                            ) : upscaleLoading ? (
                                                <LoaderCircle className="ml-2 h-4 w-4 animate-spin" />
                                            ) : (
                                                <Sparkles className="ml-2 h-4 w-4" />
                                            )}
                                        </Button>
                                    </div>
                                    {canDownloadBannerImage &&
                                        project.Banner?.downloadLink && (
                                            <DownloadAction
                                                downloadLink={
                                                    project.Banner?.downloadLink
                                                }
                                                canDownload={true}
                                                libraryImageID={
                                                    project.Banner?.libraryImage
                                                        ?.ID ?? 0
                                                }
                                            />
                                        )}
                                </div>
                            )}
                        </div>
                    </div>
                }
                {/* Content - Temporarily hidden to try out new design from Milot */}
                {/* <div className="absolute left-0 top-0 z-10 grid h-full w-full grid-cols-[59%_40%] items-end gap-[1%] px-12 pb-12">
                    <div className="flex w-full items-end justify-start"></div>

                    <div className="flex w-full items-end justify-end">
                        <p className="my-0 line-clamp-2 max-w-150 break-words text-right font-display text-16 font-[600] leading-[1] tracking-[-0.08rem] text-white">
                            <span className="mr-4 inline-block h-[6px] w-[5.5rem] -translate-y-1 bg-white align-middle"></span>
                            By {truncateString(project.Professional?.Title, 42)}
                        </p>
                    </div>
                </div> */}
            </div>
        </div>
    );
};

export default ProjectBanner;
